import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Banner, Badge, Button, Text } from "@shopify/polaris";
import { RootState, AppDispatch } from "configureStore";
import {
  fetchJobLeaveDetails,
  setDestroy,
  setData,
} from "./job-leave-view.slice";
import {
  ALL_JOB_LEAVE_STEPS,
  JOB_LEAVE_STEP_LABELS,
  getJobLeaveStatusLabel,
  getJobLeaveStatusTone,
  getJobLeaveStepIndex,
  normalizeJobLeaveStatus,
  JobLeaveStep,
} from "utils/jobLeaveStatus";
import {
  completeJobLeave,
  completeJobLeaveStep,
  reopenJobLeaveStep,
  cancelJobLeave,
  returnJobLeaveAsset,
  cancelJobLeaveSignature,
  cancelJobLeaveRequest,
  clearJobLeaveManagedUser,
  clearJobLeaveOtherAssignment,
} from "services/job-leave.service";
import { toast } from "sonner";
import PrettyDate from "components/format/PrettyDate";
import PermissionChips from "components/permission-picker/PermissionChips";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";

function JobLeaveViewContainer({
  companyId: companyIdProp,
  jobLeaveId: jobLeaveIdProp,
  embedded = false,
  hideStepBar = false,
  onProcessEnded,
  onStatusChange,
}: {
  companyId?: string;
  jobLeaveId?: string;
  embedded?: boolean;
  hideStepBar?: boolean;
  onProcessEnded?: () => void | Promise<void>;
  onStatusChange?: (status: string) => void;
} = {}) {
  const dispatch: AppDispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "en") as "lt" | "en";
  const navigate = useNavigate();
  const params = useParams<{
    companyId: string;
    jobLeaveId: string;
  }>();
  const companyId = companyIdProp || params.companyId;
  const jobLeaveId = jobLeaveIdProp || params.jobLeaveId;

  const { jobLeave, isLoading, isError } = useSelector(
    (state: RootState) => state.jobLeaveViewReducer
  );

  const [actionLoading, setActionLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    step_assets: true,
    step_leaves: true,
    step_signatures: true,
    step_permissions: true,
  });

  useEffect(() => {
    if (companyId && jobLeaveId) {
      dispatch(fetchJobLeaveDetails(companyId, jobLeaveId));
    }
    return () => {
      dispatch(setDestroy());
    };
  }, [dispatch, companyId, jobLeaveId]);

  useEffect(() => {
    if (!jobLeave) return;
    const status = normalizeJobLeaveStatus(jobLeave.status);
    const idx = getJobLeaveStepIndex(status);
    const expandAll = status === "completed" || status === "cancelled";
    setExpanded({
      step_assets: idx === 0 || expandAll,
      step_leaves: idx === 1 || expandAll,
      step_signatures: idx === 2 || expandAll,
      step_permissions: idx === 3 || expandAll,
    });
    onStatusChange?.(status);
  }, [jobLeave?.id, jobLeave?.status, onStatusChange]);

  const refresh = async () => {
    if (companyId && jobLeaveId) {
      await dispatch(fetchJobLeaveDetails(companyId, jobLeaveId));
    }
  };

  const openConfirmModal = ({
    title,
    message,
    confirmLabel,
    tone,
    onConfirm,
  }: {
    title: string;
    message: string;
    confirmLabel?: string;
    tone?: "critical" | "success";
    onConfirm: () => Promise<void>;
  }) => {
    dispatch(setModalxOptions({ title }));
    dispatch(setModalxComponent("JobLeaveConfirmModal"));
    dispatch(
      setModalxComponentProps({
        message,
        confirmLabel,
        tone,
        onConfirm,
      })
    );
  };

  const toggleSection = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleOpenAssetReturn = (assetId: string) => {
    dispatch(setModalxComponent("AssetsReturnForm"));
    dispatch(
      setModalxComponentProps({
        id: assetId,
        onSuccess: async () => {
          await refresh();
        },
      })
    );
  };

  const assetUrl = (assetId: string) =>
    `/app/${companyId}/assets/${assetId}`;

  const leaveUrl = (leaveId: string) =>
    `/app/${companyId}/leave-calendar?leave=${encodeURIComponent(leaveId)}`;

  const documentUrl = (fileId: string) =>
    `/app/${companyId}/documents/view/${fileId}`;

  const handleOpenPdfView = (fileId?: string | null, title?: string) => {
    if (!fileId || !companyId) return;
    dispatch(
      setModalxOptions({
        title: title || t("view_document_preview"),
      })
    );
    dispatch(setModalxComponent("PdfView"));
    dispatch(
      setModalxComponentProps({
        id: fileId,
        companyId,
      })
    );
  };

  const handleForceReturnAsset = (assetId: string) => {
    if (!companyId || !jobLeaveId) return;
    openConfirmModal({
      title: t("force_return_asset"),
      message: t("confirm_force_return_asset"),
      confirmLabel: t("force_return_asset"),
      tone: "critical",
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await returnJobLeaveAsset(companyId, jobLeaveId, assetId);
          await refresh();
          toast.success(t("asset_returned"));
        } catch (e: any) {
          const msg = e?.response?.data?.error || t("error");
          toast.error(typeof msg === "string" ? msg : t("error"));
          throw e;
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleRemoveSignature = (signatureId: string) => {
    if (!companyId || !jobLeaveId) return;
    const signature = jobLeave?.checklist?.pendingSignatures?.find(
      (s) => s.id === signatureId
    );
    if (!signature) return;

    dispatch(setModalxOptions({ title: t("remove_signature") }));
    dispatch(setModalxComponent("JobLeaveRemoveSignatureModal"));
    dispatch(
      setModalxComponentProps({
        signature,
        onConfirm: async () => {
          setActionLoading(true);
          try {
            await cancelJobLeaveSignature(companyId, jobLeaveId, signatureId);
            await refresh();
            toast.success(t("signature_removed"));
          } catch (e: any) {
            const msg = e?.response?.data?.error || t("error");
            toast.error(typeof msg === "string" ? msg : t("error"));
            throw e;
          } finally {
            setActionLoading(false);
          }
        },
      })
    );
  };

  const handleCancelLeave = (leaveRequestId: string) => {
    if (!companyId || !jobLeaveId) return;
    openConfirmModal({
      title: t("cancel_leave_request"),
      message: t("confirm_cancel_leave_request"),
      confirmLabel: t("cancel_leave_request"),
      tone: "critical",
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await cancelJobLeaveRequest(companyId, jobLeaveId, leaveRequestId);
          await refresh();
          toast.success(t("leave_request_cancelled"));
        } catch (e: any) {
          const msg = e?.response?.data?.error || t("error");
          toast.error(typeof msg === "string" ? msg : t("error"));
          throw e;
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleCompleteStep = () => {
    if (!companyId || !jobLeaveId || !jobLeave) return;
    openConfirmModal({
      title: t("complete_step"),
      message: t("confirm_complete_job_leave_step"),
      confirmLabel: t("complete_step"),
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const updated = await completeJobLeaveStep(companyId, jobLeaveId);
          dispatch(setData(updated));
          toast.success(t("job_leave_step_completed"));
        } catch (e: any) {
          const msg = e?.response?.data?.error || t("error");
          toast.error(typeof msg === "string" ? msg : t("error"));
          await refresh();
          throw e;
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleClearManagedUser = (userId: string, userName: string) => {
    if (!companyId || !jobLeaveId) return;
    openConfirmModal({
      title: t("clear_manager_link"),
      message: t("confirm_remove_assignment_from_section", {
        section: t("job_leave_tab_managers"),
        label: t("job_leave_manager_of"),
        name: userName,
      }),
      confirmLabel: t("clear_manager_link"),
      tone: "critical",
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await clearJobLeaveManagedUser(companyId, jobLeaveId, userId);
          await refresh();
          toast.success(t("manager_link_cleared"));
        } catch (e: any) {
          const msg = e?.response?.data?.error || t("error");
          toast.error(typeof msg === "string" ? msg : t("error"));
          throw e;
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleClearOtherAssignment = (item: {
    id: string;
    type: string;
    section?: string;
    label: string;
    name: string;
  }) => {
    if (!companyId || !jobLeaveId) return;
    const sectionKey =
      item.section === "categories" ||
      item.type === "file_category_responsible" ||
      item.type === "file_category_reviewer"
        ? "job_leave_tab_categories"
        : item.section === "departments" ||
            item.type === "department_head" ||
            item.type === "department_member"
          ? "job_leave_tab_departments"
          : "job_leave_tab_other";
    openConfirmModal({
      title: t("remove_assignment"),
      message: t("confirm_remove_assignment_from_section", {
        section: t(sectionKey),
        label: item.label,
        name: item.name,
      }),
      confirmLabel: t("remove_assignment"),
      tone: "critical",
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await clearJobLeaveOtherAssignment(
            companyId,
            jobLeaveId,
            item.type,
            item.id
          );
          await refresh();
          toast.success(t("assignment_removed"));
        } catch (e: any) {
          const msg = e?.response?.data?.error || t("error");
          toast.error(typeof msg === "string" ? msg : t("error"));
          throw e;
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleCompleteQuit = () => {
    if (!companyId || !jobLeaveId) return;
    openConfirmModal({
      title: t("complete_job_leave"),
      message: t("confirm_complete_job_leave"),
      confirmLabel: t("complete_job_leave"),
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const updated = await completeJobLeave(companyId, jobLeaveId);
          dispatch(setData(updated));
          toast.success(t("job_leave_completed"));
          if (onProcessEnded) await onProcessEnded();
        } catch (e: any) {
          const msg = e?.response?.data?.error || t("error");
          toast.error(typeof msg === "string" ? msg : t("error"));
          await refresh();
          throw e;
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleReopen = (step: JobLeaveStep) => {
    if (!companyId || !jobLeaveId) return;
    const label =
      JOB_LEAVE_STEP_LABELS[step]?.[lang] || JOB_LEAVE_STEP_LABELS[step].en;
    openConfirmModal({
      title: t("reopen_step"),
      message: t("confirm_reopen_job_leave_step", { step: label }),
      confirmLabel: t("reopen_step"),
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const updated = await reopenJobLeaveStep(
            companyId,
            jobLeaveId,
            step
          );
          dispatch(setData(updated));
          toast.success(t("job_leave_step_reopened"));
        } catch (e: any) {
          const msg = e?.response?.data?.error || t("error");
          toast.error(typeof msg === "string" ? msg : t("error"));
          throw e;
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleCancelProcess = () => {
    if (!companyId || !jobLeaveId) return;
    openConfirmModal({
      title: t("cancel_job_leave"),
      message: t("confirm_cancel_job_leave"),
      confirmLabel: t("cancel_job_leave"),
      tone: "critical",
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const updated = await cancelJobLeave(companyId, jobLeaveId);
          dispatch(setData(updated));
          toast.success(t("job_leave_cancelled"));
          if (onProcessEnded) await onProcessEnded();
        } catch (e: any) {
          const msg = e?.response?.data?.error || t("error");
          toast.error(typeof msg === "string" ? msg : t("error"));
          throw e;
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  if (isLoading && !jobLeave) {
    return (
      <div className="w-full px-6 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-40 bg-gray-200 rounded" />
          <div className="h-40 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (isError || !jobLeave) {
    return (
      <div className="w-full px-6 py-4">
        <Banner tone="critical">{t("not_fetch")}</Banner>
      </div>
    );
  }

  const checklist = jobLeave.checklist;
  const status = normalizeJobLeaveStatus(jobLeave.status);
  const currentStepIndex = getJobLeaveStepIndex(status);
  const isTerminal = status === "completed" || status === "cancelled";
  const tone = getJobLeaveStatusTone(status);
  const isActiveStep = (step: JobLeaveStep) => status === step;
  const isStepUnlocked = (step: JobLeaveStep) => {
    if (isTerminal) return true;
    const idx = ALL_JOB_LEAVE_STEPS.indexOf(step);
    return currentStepIndex >= idx;
  };
  const isStepDone = (step: JobLeaveStep) => {
    if (status === "completed") return true;
    const idx = ALL_JOB_LEAVE_STEPS.indexOf(step);
    return currentStepIndex > idx;
  };

  const canCompleteAssets =
    isActiveStep("step_assets") && !!checklist?.readyAssets;
  const canCompleteLeaves =
    isActiveStep("step_leaves") && !!checklist?.readyLeaves;
  const canCompleteSignatures =
    isActiveStep("step_signatures") && !!checklist?.readySignatures;
  const canCompleteQuit =
    isActiveStep("step_permissions") &&
    !!checklist?.readyManagers &&
    !!checklist?.readyOtherAssignments &&
    !!checklist?.readyToComplete;

  const categoryAssignments =
    checklist?.otherAssignments?.filter(
      (item) =>
        item.type === "file_category_responsible" ||
        item.type === "file_category_reviewer"
    ) || [];
  const departmentAssignments =
    checklist?.otherAssignments?.filter(
      (item) =>
        item.type === "department_head" || item.type === "department_member"
    ) || [];
  const otherSectionAssignments =
    checklist?.otherAssignments?.filter(
      (item) =>
        item.type !== "file_category_responsible" &&
        item.type !== "file_category_reviewer" &&
        item.type !== "department_head" &&
        item.type !== "department_member"
    ) || [];

  type RemovalRow = {
    key: string;
    from: string;
    name: string;
    role: string;
    onRemove?: () => void;
  };

  const removalRows: RemovalRow[] = [
    ...(checklist?.managedUsers || []).map((person) => ({
      key: `manager-${person.id}`,
      from: t("job_leave_tab_managers"),
      name: person.name,
      role: t("job_leave_manager_of"),
      onRemove: isActiveStep("step_permissions")
        ? () => handleClearManagedUser(person.id, person.name)
        : undefined,
    })),
    ...categoryAssignments.map((item) => {
      const role =
        item.type === "file_category_responsible"
          ? t("responsible_user")
          : t("review_required_by");
      return {
        key: `category-${item.type}-${item.id}`,
        from: t("job_leave_tab_categories"),
        name: item.name,
        role,
        onRemove: isActiveStep("step_permissions")
          ? () =>
              handleClearOtherAssignment({
                ...item,
                label: role,
              })
          : undefined,
      };
    }),
    ...departmentAssignments.map((item) => {
      const role =
        item.type === "department_head" ? t("head") : t("members");
      return {
        key: `department-${item.type}-${item.id}`,
        from: t("job_leave_tab_departments"),
        name: item.name,
        role,
        onRemove: isActiveStep("step_permissions")
          ? () =>
              handleClearOtherAssignment({
                ...item,
                label: role,
              })
          : undefined,
      };
    }),
    ...otherSectionAssignments.map((item) => ({
      key: `other-${item.type}-${item.id}`,
      from: t("job_leave_tab_other"),
      name: item.name,
      role: item.label,
      onRemove: isActiveStep("step_permissions")
        ? () => handleClearOtherAssignment(item)
        : undefined,
    })),
  ];

  const reopenTargets =
    !isTerminal && currentStepIndex > 0
      ? ALL_JOB_LEAVE_STEPS.slice(0, currentStepIndex)
      : [];

  const sectionHeader = (
    step: JobLeaveStep,
    count: number,
    locked: boolean
  ) => {
    const label =
      JOB_LEAVE_STEP_LABELS[step]?.[lang] || JOB_LEAVE_STEP_LABELS[step].en;
    const stepNum = ALL_JOB_LEAVE_STEPS.indexOf(step) + 1;
    const open = expanded[step];
    return (
      <button
        type="button"
        className="w-full flex items-center justify-between text-left"
        onClick={() => toggleSection(step)}
        disabled={locked}
      >
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">
            {stepNum}. {label}
          </h2>
          <Badge
            tone={
              isStepDone(step)
                ? "success"
                : isActiveStep(step)
                  ? "info"
                  : "new"
            }
          >
            {isStepDone(step)
              ? t("completed")
              : isActiveStep(step)
                ? t("current_step")
                : locked
                  ? t("locked")
                  : t("pending")}
          </Badge>
          <span className="text-sm text-gray-500">({count})</span>
        </div>
        {!locked && (
          <span className="text-gray-500 text-sm" aria-hidden>
            {open ? "▲" : "▼"}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className={embedded ? "py-1" : "px-6 py-4"}>
      {!hideStepBar && (
        <div
          className={
            embedded
              ? "sticky top-0 z-10 pb-3 mb-3 bg-gray-50"
              : "sticky top-0 z-20 -mx-6 px-6 pt-4 pb-4 bg-gray-50 border-b border-gray-200"
          }
        >
          {!embedded && jobLeave.onboarding && (
            <button
              type="button"
              className="text-sm text-blue-600 hover:underline mb-2"
              onClick={() =>
                navigate(`/app/${companyId}/onboarding/${jobLeave.onboarding}`)
              }
            >
              ← {t("hr_module")}
            </button>
          )}
          {!embedded && (
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-semibold">
                    {jobLeave.user?.name || t("job_leave")}
                  </h1>
                  <Badge tone={tone}>
                    {getJobLeaveStatusLabel(status, t, lang)}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {jobLeave.user?.email || ""}
                </p>
              </div>
              {!isTerminal && (
                <div className="flex flex-wrap gap-2">
                  {reopenTargets.map((step) => (
                    <Button
                      key={step}
                      onClick={() => handleReopen(step)}
                      disabled={actionLoading}
                    >
                      {t("reopen_to")}{" "}
                      {JOB_LEAVE_STEP_LABELS[step]?.[lang] ||
                        JOB_LEAVE_STEP_LABELS[step].en}
                    </Button>
                  ))}
                  <Button
                    tone="critical"
                    onClick={handleCancelProcess}
                    disabled={actionLoading}
                  >
                    {t("cancel_job_leave")}
                  </Button>
                </div>
              )}
            </div>
          )}
          <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
            <div className="flex items-center gap-2 flex-wrap">
              {ALL_JOB_LEAVE_STEPS.map((step, idx) => {
                const stepLabel =
                  JOB_LEAVE_STEP_LABELS[step]?.[lang] ||
                  JOB_LEAVE_STEP_LABELS[step].en;
                const isCurrent = status === step;
                const isDone = currentStepIndex > idx || status === "completed";
                return (
                  <div
                    key={step}
                    className={`px-3 py-1.5 rounded-lg min-w-[80px] text-center ${
                      isCurrent
                        ? "bg-blue-500 text-white"
                        : isDone
                          ? "bg-green-500 text-white"
                          : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    <p className={`text-sm ${isCurrent ? "font-bold" : ""}`}>
                      {`${idx + 1}. ${stepLabel}`}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Summary info — directly under status bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 flex-1 min-w-0">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t("status")}
              </p>
              <div className="mt-1">
                <Badge tone={tone}>
                  {getJobLeaveStatusLabel(status, t, lang)}
                </Badge>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t("started_by", { defaultValue: "Started by" })}
              </p>
              <p className="mt-1 text-sm text-gray-900">
                {jobLeave.createdBy?.name ||
                  jobLeave.createdBy?.email ||
                  "-"}
              </p>
              {jobLeave.createdAt ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  <PrettyDate
                    dateString={new Date(jobLeave.createdAt).toISOString()}
                  />
                </p>
              ) : null}
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t("quit_date")}
              </p>
              <p className="mt-1 text-sm text-gray-900">
                {jobLeave.quitDate ? (
                  <PrettyDate
                    dateString={new Date(jobLeave.quitDate).toISOString()}
                  />
                ) : (
                  "-"
                )}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {status === "completed"
                  ? t("completed_by", { defaultValue: "Completed by" })
                  : t("employee")}
              </p>
              <p className="mt-1 text-sm text-gray-900">
                {status === "completed"
                  ? jobLeave.completedBy?.name ||
                    jobLeave.completedBy?.email ||
                    "-"
                  : jobLeave.user?.name || jobLeave.user?.email || "-"}
              </p>
              {status === "completed" && jobLeave.completedAt ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  <PrettyDate
                    dateString={new Date(jobLeave.completedAt).toISOString()}
                  />
                </p>
              ) : null}
            </div>
          </div>
          {!isTerminal && (
            <div className="flex flex-wrap gap-2 shrink-0">
              {reopenTargets.map((step) => (
                <Button
                  key={step}
                  onClick={() => handleReopen(step)}
                  disabled={actionLoading}
                >
                  {t("reopen_to")}{" "}
                  {JOB_LEAVE_STEP_LABELS[step]?.[lang] ||
                    JOB_LEAVE_STEP_LABELS[step].en}
                </Button>
              ))}
              <Button
                tone="critical"
                onClick={handleCancelProcess}
                disabled={actionLoading}
              >
                {t("cancel_job_leave")}
              </Button>
            </div>
          )}
        </div>
        {jobLeave.notes ? (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
              {t("notes")}
            </p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {jobLeave.notes}
            </p>
          </div>
        ) : null}
      </div>

      <div className="space-y-4">
        {status === "completed" && (
          <Banner tone="success">{t("job_leave_completed_message")}</Banner>
        )}
        {status === "cancelled" && (
          <Banner tone="warning">{t("job_leave_cancelled")}</Banner>
        )}

        {/* Step 1 — Assets */}
        <section
          className={`bg-white rounded-lg border shadow-sm p-4 ${
            !isStepUnlocked("step_assets")
              ? "border-gray-100 opacity-60"
              : isActiveStep("step_assets")
                ? "border-blue-300"
                : "border-gray-200"
          }`}
        >
          {sectionHeader(
            "step_assets",
            checklist?.assignedAssets?.length ?? 0,
            !isStepUnlocked("step_assets")
          )}
          {isStepUnlocked("step_assets") && expanded.step_assets && (
            <div className="mt-4 space-y-3">
              {isActiveStep("step_assets") && !checklist?.readyAssets && (
                <Banner tone="warning">{t("job_leave_assets_not_ready")}</Banner>
              )}
              {isActiveStep("step_assets") && checklist?.readyAssets && (
                <Banner tone="success">{t("job_leave_assets_ready")}</Banner>
              )}
              {!checklist?.assignedAssets?.length ? (
                <p className="text-sm text-gray-500">{t("no_assigned_assets")}</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {checklist.assignedAssets.map((asset) => (
                    <li
                      key={asset.id}
                      className="py-3 flex flex-wrap items-center justify-between gap-2"
                    >
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          className="text-sm font-medium text-blue-600 hover:underline text-left"
                          onClick={() => navigate(assetUrl(asset.id))}
                        >
                          {asset.name}
                        </button>
                        <div className="text-xs text-gray-500">
                          {[
                            asset.type?.title,
                            asset.brand,
                            asset.model,
                            asset.serial,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          {asset.notReturned ? ` · ${t("not_returned")}` : ""}
                        </div>
                        {asset.latestFile && (
                          <button
                            type="button"
                            className="mt-1 text-sm text-blue-600 hover:underline truncate max-w-full text-left"
                            onClick={() =>
                              handleOpenPdfView(
                                asset.latestFile?.id,
                                asset.latestFile?.title
                              )
                            }
                          >
                            {asset.latestFile.title || t("view_pdf")}
                            {asset.latestFile.subject
                              ? ` · ${
                                  asset.latestFile.subject === "return"
                                    ? t("return_asset")
                                    : t("assign")
                                }`
                              : ""}
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="slim"
                          onClick={() => navigate(assetUrl(asset.id))}
                        >
                          {t("open_asset")}
                        </Button>
                        {asset.latestFile && (
                          <>
                            <Button
                              size="slim"
                              onClick={() =>
                                handleOpenPdfView(
                                  asset.latestFile?.id,
                                  asset.latestFile?.title
                                )
                              }
                            >
                              {t("view_pdf")}
                            </Button>
                            <Button
                              size="slim"
                              onClick={() =>
                                navigate(documentUrl(asset.latestFile!.id))
                              }
                            >
                              {t("go_to_document")}
                            </Button>
                          </>
                        )}
                        {isActiveStep("step_assets") && (
                          <>
                            {asset.canStartReturnDocument && (
                              <Button
                                size="slim"
                                variant="primary"
                                onClick={() => handleOpenAssetReturn(asset.id)}
                                disabled={actionLoading}
                              >
                                {t("return_asset")}
                              </Button>
                            )}
                            <Button
                              size="slim"
                              tone="critical"
                              onClick={() => handleForceReturnAsset(asset.id)}
                              disabled={actionLoading}
                            >
                              {t("force_return_asset")}
                            </Button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {isActiveStep("step_assets") && (
                <div className="pt-2 flex justify-end">
                  <Button
                    variant="primary"
                    onClick={handleCompleteStep}
                    disabled={!canCompleteAssets || actionLoading}
                    loading={actionLoading}
                  >
                    {t("complete_assets_step")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Step 2 — Leave requests */}
        <section
          className={`bg-white rounded-lg border shadow-sm p-4 ${
            !isStepUnlocked("step_leaves")
              ? "border-gray-100 opacity-60"
              : isActiveStep("step_leaves")
                ? "border-blue-300"
                : "border-gray-200"
          }`}
        >
          {sectionHeader(
            "step_leaves",
            checklist?.openLeaveRequests?.length ?? 0,
            !isStepUnlocked("step_leaves")
          )}
          {isStepUnlocked("step_leaves") && expanded.step_leaves && (
            <div className="mt-4 space-y-3">
              <p className="text-xs text-gray-500">
                {t("open_leave_requests_hint")}
              </p>
              {isActiveStep("step_leaves") && !checklist?.readyLeaves && (
                <Banner tone="warning">{t("job_leave_leaves_not_ready")}</Banner>
              )}
              {isActiveStep("step_leaves") && checklist?.readyLeaves && (
                <Banner tone="success">{t("job_leave_leaves_ready")}</Banner>
              )}
              {!checklist?.openLeaveRequests?.length ? (
                <p className="text-sm text-gray-500">
                  {t("no_open_leave_requests")}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {checklist.openLeaveRequests.map((leave) => (
                    <li
                      key={leave.id}
                      className="py-3 flex flex-wrap items-center justify-between gap-2"
                    >
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          className="text-sm font-medium text-blue-600 hover:underline text-left"
                          onClick={() => navigate(leaveUrl(leave.id))}
                        >
                          {leave.leaveType}
                        </button>
                        <div className="text-xs text-gray-500">
                          {leave.startDate} → {leave.endDate} · {leave.status}
                        </div>
                        {leave.file && (
                          <button
                            type="button"
                            className="mt-1 text-sm text-blue-600 hover:underline truncate max-w-full text-left"
                            onClick={() =>
                              handleOpenPdfView(leave.file?.id, leave.file?.title)
                            }
                          >
                            {leave.file.title || t("view_pdf")}
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="slim"
                          onClick={() => navigate(leaveUrl(leave.id))}
                        >
                          {t("open_leave")}
                        </Button>
                        {leave.file && (
                          <>
                            <Button
                              size="slim"
                              onClick={() =>
                                handleOpenPdfView(
                                  leave.file?.id,
                                  leave.file?.title
                                )
                              }
                            >
                              {t("view_pdf")}
                            </Button>
                            <Button
                              size="slim"
                              onClick={() =>
                                navigate(documentUrl(leave.file!.id))
                              }
                            >
                              {t("go_to_document")}
                            </Button>
                          </>
                        )}
                        {isActiveStep("step_leaves") && (
                          <Button
                            size="slim"
                            tone="critical"
                            onClick={() => handleCancelLeave(leave.id)}
                            disabled={actionLoading}
                          >
                            {t("cancel_leave_request")}
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {isActiveStep("step_leaves") && (
                <div className="pt-2 flex justify-end">
                  <Button
                    variant="primary"
                    onClick={handleCompleteStep}
                    disabled={!canCompleteLeaves || actionLoading}
                    loading={actionLoading}
                  >
                    {t("complete_leaves_step")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Step 3 — Signatures */}
        <section
          className={`bg-white rounded-lg border shadow-sm p-4 ${
            !isStepUnlocked("step_signatures")
              ? "border-gray-100 opacity-60"
              : isActiveStep("step_signatures")
                ? "border-blue-300"
                : "border-gray-200"
          }`}
        >
          {sectionHeader(
            "step_signatures",
            checklist?.pendingSignatures?.length ?? 0,
            !isStepUnlocked("step_signatures")
          )}
          {isStepUnlocked("step_signatures") && expanded.step_signatures && (
            <div className="mt-4 space-y-3">
              {isActiveStep("step_signatures") &&
                !checklist?.readySignatures && (
                  <Banner tone="warning">
                    {t("job_leave_signatures_not_ready")}
                  </Banner>
                )}
              {isActiveStep("step_signatures") &&
                checklist?.readySignatures && (
                  <Banner tone="success">
                    {t("job_leave_signatures_ready")}
                  </Banner>
                )}
              {!checklist?.pendingSignatures?.length ? (
                <p className="text-sm text-gray-500">
                  {t("no_pending_signatures")}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {checklist.pendingSignatures.map((sig) => (
                    <li
                      key={sig.id}
                      className="py-3 flex flex-wrap items-center justify-between gap-2"
                    >
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          className={`text-sm font-medium text-left truncate max-w-full ${
                            sig.file?.id
                              ? "text-blue-600 hover:underline"
                              : ""
                          }`}
                          onClick={() =>
                            handleOpenPdfView(sig.file?.id, sig.file?.title)
                          }
                          disabled={!sig.file?.id}
                        >
                          {sig.file?.title || sig.id}
                        </button>
                        <div className="text-xs text-gray-500">
                          {sig.file?.signType || ""}{" "}
                          {sig.position ? `· ${sig.position}` : ""}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {sig.file?.id && (
                          <>
                            <Button
                              size="slim"
                              onClick={() =>
                                handleOpenPdfView(
                                  sig.file?.id,
                                  sig.file?.title
                                )
                              }
                            >
                              {t("view_pdf")}
                            </Button>
                            <Button
                              size="slim"
                              onClick={() =>
                                navigate(documentUrl(sig.file!.id))
                              }
                            >
                              {t("go_to_document")}
                            </Button>
                          </>
                        )}
                        {isActiveStep("step_signatures") && (
                          <Button
                            size="slim"
                            tone="critical"
                            onClick={() => handleRemoveSignature(sig.id)}
                            disabled={actionLoading}
                          >
                            {t("remove_signature")}
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {isActiveStep("step_signatures") && (
                <div className="pt-2 flex justify-end">
                  <Button
                    variant="primary"
                    onClick={handleCompleteStep}
                    disabled={!canCompleteSignatures || actionLoading}
                    loading={actionLoading}
                  >
                    {t("complete_signatures_step")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Step 4 — Permissions */}
        <section
          className={`bg-white rounded-lg border shadow-sm p-4 ${
            !isStepUnlocked("step_permissions")
              ? "border-gray-100 opacity-60"
              : isActiveStep("step_permissions")
                ? "border-blue-300"
                : "border-gray-200"
          }`}
        >
          {sectionHeader(
            "step_permissions",
            (checklist?.permissions?.length ?? 0) +
              (checklist?.managedUsers?.length ?? 0) +
              (checklist?.otherAssignments?.length ?? 0),
            !isStepUnlocked("step_permissions")
          )}
          {isStepUnlocked("step_permissions") && expanded.step_permissions && (
            <div className="mt-4 space-y-3">
              {isActiveStep("step_permissions") &&
                !(
                  checklist?.readyManagers &&
                  checklist?.readyOtherAssignments
                ) && (
                  <Banner tone="warning">
                    {t("job_leave_permissions_not_ready")}
                  </Banner>
                )}
              {isActiveStep("step_permissions") &&
                checklist?.readyManagers &&
                checklist?.readyOtherAssignments && (
                  <Banner tone="success">
                    {t("job_leave_permissions_ready")}
                  </Banner>
                )}

              {/* Rights are informational: they all end with the leave, nothing to remove by hand. */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {t("job_leave_tab_permissions")}
                </p>
                {checklist?.permissions?.length ? (
                  <PermissionChips permissions={checklist.permissions} />
                ) : (
                  <p className="text-sm text-gray-500">{t("no_user_permissions_provided")}</p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  {t("job_leave_permissions_end_with_leave")}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {t("job_leave_removal_list")}
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  {t("job_leave_removal_list_hint")}
                </p>
                {!removalRows.length ? (
                  <p className="text-sm text-gray-500">
                    {t("job_leave_removal_list_empty")}
                  </p>
                ) : (
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-3 py-2 font-medium">
                            {t("job_leave_removal_from")}
                          </th>
                          <th className="px-3 py-2 font-medium">
                            {t("job_leave_removal_item")}
                          </th>
                          <th className="px-3 py-2 font-medium">
                            {t("job_leave_removal_role")}
                          </th>
                          <th className="px-3 py-2 font-medium text-right">
                            {t("actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {removalRows.map((row) => (
                          <tr key={row.key}>
                            <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                              {row.from}
                            </td>
                            <td className="px-3 py-3 font-medium text-gray-900">
                              {row.name}
                            </td>
                            <td className="px-3 py-3 text-gray-600">
                              {row.role}
                            </td>
                            <td className="px-3 py-3 text-right">
                              {row.onRemove ? (
                                <Button
                                  size="slim"
                                  tone="critical"
                                  onClick={row.onRemove}
                                  disabled={actionLoading}
                                >
                                  {t("remove_assignment")}
                                </Button>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {isActiveStep("step_permissions") && (
                <div className="pt-2 flex justify-end">
                  <Button
                    variant="primary"
                    onClick={handleCompleteQuit}
                    disabled={!canCompleteQuit || actionLoading}
                    loading={actionLoading}
                  >
                    {t("complete_job_leave")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default JobLeaveViewContainer;
